// src/components/Printing.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import QR from "../../assets/img/QR.png";
import aituQR from "../../assets/img/aitu-qr.png";

// Каналы, через которые пользователь отправляет файл боту
const MESSENGERS = [
    { src: QR, title: "Telegram", altKey: "qr_alt", handleKey: "bot_name" },
    { src: aituQR, title: "Aitu", altKey: "aitu_qr_alt", handleKey: "aitu_name" },
];

// Иконки шагов — по одной на карточку карусели
const STEP_ICONS = [
    // 1 — QR бота
    <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <path d="M14 14h3v3h-3zM18 18h3v3h-3z" />
    </>,
    // 2 — отправка файла
    <>
        <path d="M22 2L11 13" />
        <path d="M22 2l-7 20-4-9-9-4z" />
    </>,
    // 3 — код в чате
    <>
        <rect x="3" y="8" width="18" height="10" rx="3" />
        <path d="M7 13h.01M11 13h.01M15 13h.01M19 13h-2" />
    </>,
    // 4 — ввод кода на киоске
    <>
        <rect x="4" y="2" width="16" height="20" rx="3" />
        <path d="M9 7h6M9 11h6M9 15h3" />
    </>,
    // 5 — настройка и оплата
    <>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14 3h-4l-.6 2.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2L10 21h4l.6-2.7a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.06-.4.1-.8.1-1.2z" />
    </>,
    // 6 — выдача из лотка
    <>
        <path d="M12 3v10M8 9l4 4 4-4" />
        <path d="M4 17h16v4H4z" />
    </>,
];

const STEP_COUNT = STEP_ICONS.length;
const STEP_INTERVAL = 1900; // мс на шаг, как в макете
const VISIBLE_STEPS = 3; // сколько карточек влезает в кадр

// Ширина карточки и шаг сдвига считаются от ширины кадра (ряд растянут на 100%),
// поэтому проценты в translateX и в width ссылаются на одну и ту же базу.
const CARD_WIDTH = `calc((100% - 2.8vw) / ${VISIBLE_STEPS})`;
const CARD_STEP = `calc((100% - 2.8vw) / ${VISIBLE_STEPS} + 1.4vw)`;

const blockVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.15 },
    }),
};

function Printing() {
    const { t } = useTranslation();
    const [liveStep, setLiveStep] = useState(0);

    useEffect(() => {
        const timer = setInterval(
            () => setLiveStep((step) => (step + 1) % STEP_COUNT),
            STEP_INTERVAL,
        );
        return () => clearInterval(timer);
    }, []);

    // Держим активную карточку в кадре: она идёт второй из трёх видимых.
    const shift = Math.max(
        0,
        Math.min(liveStep - 1, STEP_COUNT - VISIBLE_STEPS),
    );

    // Заголовок вида «Печать за 6 шагов» с синим акцентом на подстроке.
    const title = t("printing.title");
    const accent = t("printing.title_accent");
    const accentAt = title.indexOf(accent);

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col justify-center gap-[2vh] bg-[#EDEFF4]">
            {/* Карусель шагов */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] p-[3vh_2.4vw]"
            >
                <h2 className="text-[2.8vh] font-extrabold tracking-tight mb-[2.2vh]">
                    {accentAt === -1 ? (
                        title
                    ) : (
                        <>
                            {title.slice(0, accentAt)}
                            <span className="text-[#246CE2]">{accent}</span>
                            {title.slice(accentAt + accent.length)}
                        </>
                    )}
                </h2>

                {/* overflow-hidden нужен только для горизонтального слайда (translateX);
                    "overflow-x-hidden" без явного overflow-y на некоторых движках
                    вычисляется в overflow-y:auto (не visible — см. спеку CSS Overflow),
                    а не в honest "visible", и всё равно подрезает поднятую активную
                    карточку. Вместо этого держим overflow-hidden как есть, но даём
                    запас сверху (padding), компенсированный отрицательным margin —
                    так поднятая карточка (-translate-y-0.8vh) физически не доходит
                    до верхней границы обрезки ни в одном браузере. */}
                <div className="overflow-hidden pt-[1.2vh] -mt-[1.2vh]">
                    <div
                        className="flex gap-[1.4vw] w-full transition-transform duration-500 ease-out"
                        style={{
                            transform: `translateX(calc(-${shift} * ${CARD_STEP}))`,
                        }}
                    >
                        {STEP_ICONS.map((icon, i) => {
                            const live = i === liveStep;
                            return (
                                <div
                                    key={i}
                                    style={{ width: CARD_WIDTH }}
                                    className={`flex-none rounded-[2.4vh] p-[2.6vh_1.2vw] flex flex-col items-center text-center gap-[1.6vh] transition-all duration-300 ${
                                        live
                                            ? "bg-[#246CE2] -translate-y-[0.8vh]"
                                            : "bg-[#F1F3F7]"
                                    }`}
                                >
                                    <div
                                        className={`w-[6vh] h-[6vh] rounded-full flex-none flex items-center justify-center transition-colors duration-300 ${
                                            live ? "bg-white/20" : "bg-white"
                                        }`}
                                    >
                                        <svg
                                            className="w-[2.8vh] h-[2.8vh]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={live ? "#fff" : "#246CE2"}
                                            strokeWidth="1.9"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            {icon}
                                        </svg>
                                    </div>
                                    <b
                                        className={`text-[1.3vh] font-extrabold tracking-[0.15vh] uppercase ${
                                            live
                                                ? "text-white/70"
                                                : "text-[#7B8290]"
                                        }`}
                                    >
                                        {t("printing.step_label")} {i + 1}
                                    </b>
                                    <span
                                        className={`text-[1.7vh] font-bold leading-snug ${
                                            live ? "text-white" : ""
                                        }`}
                                    >
                                        {t(`printing.step${i + 1}`)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Прогресс по шагам */}
                <div className="h-[0.9vh] bg-[#F1F3F7] rounded-full mt-[2.4vh] overflow-hidden">
                    <div
                        className="h-full bg-[#246CE2] rounded-full transition-[width] duration-500 ease-out"
                        style={{
                            width: `${((liveStep + 1) / STEP_COUNT) * 100}%`,
                        }}
                    />
                </div>
            </motion.div>

            {/* QR бота + пояснение и переход к вводу кода */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] p-[3vh_2.4vw] flex items-center gap-[2.8vw]"
            >
                <div className="flex-none flex flex-col gap-[2vh]">
                    {MESSENGERS.map((m) => (
                        <div
                            key={m.title}
                            className="flex flex-col items-center gap-[1vh]"
                        >
                            <img
                                src={m.src}
                                alt={t(`printing.${m.altKey}`)}
                                className="w-[26vw] h-[26vw] rounded-[2.2vh] border-[0.3vh] border-[#E1E4EB] p-[1vh] bg-white"
                            />
                            <div className="flex flex-col items-center text-center">
                                <b className="text-[1.7vh] font-extrabold">
                                    {m.title}
                                </b>
                                <span className="text-[1.35vh] font-semibold text-[#7B8290]">
                                    {t(`printing.${m.handleKey}`)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex-1 min-w-0 ">
                    <h3 className="text-[2.2vh] font-extrabold text-center tracking-tight leading-tight">
                        {t("printing.note_title")}
                    </h3>
                    <p className="text-[1.5vh] font-semibold text-[#7B8290] text-center mt-[1.2vh] leading-relaxed">
                        {t("printing.note_text")}
                    </p>
                    <Link
                        to="/printing/verify"
                        state={{ from: "/printing" }}
                        className="mt-[2.4vh] w-full bg-[#246CE2] rounded-full py-[1.6vh] px-[1.2vw] flex items-center justify-center gap-[0.8vw] text-white font-bold text-[1.6vh] whitespace-nowrap"
                    >
                        {t("printing.have_code")}
                        <svg
                            className="w-[2.2vh] h-[2.2vh] flex-none"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}

export default Printing;
